/** Supabase auth session. */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../data/supabase.ts';
import { setGeminiKey } from '../data/gemini.ts';
import { writeString } from '../data/storage.ts';
import { clearLeague } from '../data/league.ts';
import { clearAchievements } from '../data/achievements.ts';
import { CHAT_LANGS, LEGACY_CHAT_HISTORY_KEY, chatHistoryKey } from '../core/tutor.ts';

interface SessionValue {
  session: Session | null;
  loading: boolean;
}

const SessionContext = createContext<SessionValue>({ session: null, loading: true });

export function SessionProvider({ children }: { children: ReactNode }) {
  const [value, setValue] = useState<SessionValue>({ session: null, loading: true });

  useEffect(() => {
    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (active) setValue({ session: data.session, loading: false });
    });
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      setValue({ session, loading: false });
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, []);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  return useContext(SessionContext);
}

/** Signs out and forgets per-person device data (the Gemini key and tutor chat). */
export async function signOut(): Promise<void> {
  try {
    await supabase.auth.signOut();
  } finally {
    setGeminiKey(null);
    writeString(LEGACY_CHAT_HISTORY_KEY, null);
    for (const lang of CHAT_LANGS) writeString(chatHistoryKey(lang), null);
    clearLeague();
    clearAchievements();
  }
}

/** Russian text for common Supabase auth errors. */
export function authErrorText(message: string): string {
  if (/invalid login credentials/i.test(message)) return 'Неверная почта или пароль.';
  if (/already registered|already been registered/i.test(message)) return 'Такой пользователь уже есть. Войдите.';
  if (/email not confirmed/i.test(message)) return 'Подтвердите почту: ссылка уже в письме.';
  if (/password should be at least/i.test(message)) return 'Пароль слишком короткий: нужно минимум 6 символов.';
  if (/rate limit|too many|security purposes/i.test(message)) return 'Слишком много попыток. Подождите минуту.';
  if (/token has expired|invalid.*(otp|token)/i.test(message)) return 'Код неверный или устарел. Запросите новый.';
  if (/fetch|network/i.test(message)) return 'Нет связи с сервером. Проверьте интернет.';
  return 'Не получилось. Попробуйте ещё раз.';
}
